/** @file moveGizmo: the move tool's three arrows, three plane squares and the screen-aligned centre square. */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { GIZMO_METRICS as M } from './gizmoMetrics';
import type { GizmoHandle, GizmoShape } from './gizmoShape';
import { gizmoUnit } from './gizmoUnit';
import { unitSquare } from './unitSquare';

/** The plane square's span along one axis, on the side `sign` (+1 or -1). */
const span = (sign: number): [number, number] => {
  const half = gizmoUnit(M.planeSize) / 2;
  return [sign * (M.planeAt - half), sign * (M.planeAt + half)];
};

/** A point at `t` along unit axis `k`. */
const on = (k: number, t: number): Vec3 => [k === 0 ? t : 0, k === 1 ? t : 0, k === 2 ? t : 0];

/**
 * Centre (3): a view-plane square `centreSquare` wide, listed first so it wins over the shafts. Arrow `k`: the line
 * from `shaftFrom` to the cone tip on axis `k`. Plane `4 + k` (normal = axis `k`): a `planeSize` square centred
 * `planeAt` along the other two axes, on the sides `signs` gives (`planeSigns`: toward the viewer).
 */
export function moveGizmo(signs: Vec3 = [1, 1, 1]): GizmoShape[] {
  const tip = 1 + gizmoUnit(M.coneLength);
  const arrows = ([0, 1, 2] as const).map((axis): GizmoShape => ({ axis, frame: 'axes', lines: [[on(axis, M.shaftFrom), on(axis, tip)]], quads: [] }));
  const planes = ([0, 1, 2] as const).map((n): GizmoShape => {
    const u = (n + 1) % 3;
    const v = (n + 2) % 3;
    return { axis: (4 + n) as GizmoHandle, frame: 'axes', lines: [], quads: [unitSquare(u, v, span(signs[u] ?? 1), span(signs[v] ?? 1))] };
  });
  const c = gizmoUnit(M.centreSquare) / 2;
  const centre = unitSquare(0, 1, [-c, c], [-c, c]);
  return [{ axis: 3, frame: 'screen', lines: [[...centre, [-c, -c, 0]]], quads: [centre] }, ...arrows, ...planes];
}
